'use client';

import type { ReactNode } from 'react';
import React from 'react';
import Image from 'next/image';
import { Box, Flex, Container, Heading, Text, Stack, HStack, Icon, Separator, VStack, Tooltip, Link } from '@skin-seoul/design-system';
import { SafeLink } from '@/components/ui/SafeLink';
import PaymentMethods from '@/components/layout/Footer/PaymentMethods';
import LucideIcon from '@/components/ui/LucideIcon';
import { supportLinks } from './constants/supportLinks';
import { followUsLinks } from './constants/followUsLinks';
import { getLayoutOptions } from '@/utils/layout/layoutFilter';
import { usePathname } from 'next/navigation';
import Subscribe from '../Subscribe';

// Mobile footer padding to accommodate sticky "Add to Bag" button on PDP as it was causing layout issues
// Accounts for: bottom navigation (60px) + sticky button height and offset (~60px)
const MOBILE_FOOTER_PADDING_BOTTOM = 120;
const ArrowNavItem = ({
  children
}: {
  children: ReactNode;
}) => {
  return <HStack alignItems="center" cursor="pointer" paddingY="4px" gap="0" position="relative" paddingLeft="16px" data-sentry-element="HStack" data-sentry-component="ArrowNavItem" data-sentry-source-file="Footer.tsx">
      <Stack position="absolute" left="-1" top="0" height="100%" justifyContent="center" data-sentry-element="Stack" data-sentry-source-file="Footer.tsx">
        <LucideIcon name="ChevronRight" boxSize="20px" color="white" data-sentry-element="LucideIcon" data-sentry-source-file="Footer.tsx" />
      </Stack>
      <Text color="generic.0" transition="all 0.1s" textStyle="label.large" _hover={{
      color: 'neutral.200'
    }} data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
        {children}
      </Text>
    </HStack>;
};
const Footer = () => {
  const pathname = usePathname();
  const {
    footer
  } = getLayoutOptions(pathname);
  if (!footer) {
    return null;
  }
  return <Flex alignItems="center" as="footer" backgroundColor="#000" flexDirection="column" justifyContent="center" position="relative" paddingBottom={{
    base: `calc(${MOBILE_FOOTER_PADDING_BOTTOM}px + env(safe-area-inset-bottom))`,
    tablet: '0'
  }} data-sentry-element="Flex" data-sentry-component="Footer" data-sentry-source-file="Footer.tsx">
      <Box as="div" mx="auto" width="100%" paddingX={{
      base: '16px',
      tablet: 4
    }} data-sentry-element="Box" data-sentry-source-file="Footer.tsx">
        <Flex flexDirection="column" maxWidth="1550px" mx="auto" gap={8} data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
          <Box data-sentry-element="Box" data-sentry-source-file="Footer.tsx">
            <Flex align="center" justify="center" paddingY="28px" data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
              <PaymentMethods data-sentry-element="PaymentMethods" data-sentry-source-file="Footer.tsx" />
            </Flex>
          </Box>
          <Separator borderColor="generic.0/20" orientation="horizontal" width="100%" data-sentry-element="Separator" data-sentry-source-file="Footer.tsx" />
          <Subscribe data-sentry-element="Subscribe" data-sentry-source-file="Footer.tsx" />
          <Separator borderColor="generic.0/20" orientation="horizontal" width="100%" data-sentry-element="Separator" data-sentry-source-file="Footer.tsx" />
          <Container maxWidth="1280px" py="60px" width="100%" paddingX={0} data-sentry-element="Container" data-sentry-source-file="Footer.tsx">
            <Flex direction={{
            base: 'column',
            tablet: 'row'
          }} flexWrap={{
            tablet: 'wrap',
            desktop: 'initial'
          }} gap={{
            base: '30px',
            tablet: 5,
            desktop: 11
          }} data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
              <Flex color="white" flexDirection="column" width={{
              mobile: '100%',
              tablet: '100%',
              desktop: '40%'
            }} gap={5} data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
                <Image alt="SkinSeoul Footer Logo" height={58} src="https://skin-seoul.com/wp-content/uploads/2024/12/desktop-logo.png" width={252} data-sentry-element="Image" data-sentry-source-file="Footer.tsx" />
                <Text textStyle="paragraph.medium" data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                  Explore the best in Korean skincare with our extensive range of 500+ top-brand products. From
                  hydrating toners to powerful serums, our unbeatable prices and latest collections cater to all your
                  skincare needs. Elevate your routine with SkinSeoul and discover the transformative power of K-beauty
                  today!
                </Text>

                <HStack alignItems="center" display="flex" gap={3} data-sentry-element="HStack" data-sentry-source-file="Footer.tsx">
                  <Heading as="h4" color="white" textStyle="paragraph.large" fontWeight="medium" data-sentry-element="Heading" data-sentry-source-file="Footer.tsx">
                    Follow Us:
                  </Heading>
                  <HStack alignItems="center" display="flex" gap="14px" data-sentry-element="HStack" data-sentry-source-file="Footer.tsx">
                    {followUsLinks.map(link => <Icon _hover={{
                    transform: 'scale(0.94)'
                  }} as="a" border="1px solid" borderColor="white" borderRadius="15px" color="white" cursor="pointer" height="30px" href={link.link} key={link.link} padding="6px" transition="all 0.2s" width="30px">
                        {link.icon}
                      </Icon>)}
                  </HStack>
                </HStack>
              </Flex>

              <Flex width={{
              mobile: '32%',
              tablet: '30%',
              desktop: '30%'
            }} height="fit-content" flexDirection="column" data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
                <Heading as="h4" color="generic.0" marginBottom="16px" textStyle="paragraph.medium" fontWeight="medium" textTransform="uppercase" data-sentry-element="Heading" data-sentry-source-file="Footer.tsx">
                  Support
                </Heading>
                <Stack gap={2} data-sentry-element="Stack" data-sentry-source-file="Footer.tsx">
                  {supportLinks.map(link => <SafeLink key={link.href} href={link.href}>
                      <ArrowNavItem>{link.label}</ArrowNavItem>
                    </SafeLink>)}
                </Stack>
              </Flex>

              <Flex color="white" width={{
              mobile: '31%',
              tablet: '30%',
              desktop: '30%'
            }} flexDirection="column" data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
                <Heading as="h4" marginBottom={3} textStyle="paragraph.medium" fontWeight="medium" textTransform="uppercase" data-sentry-element="Heading" data-sentry-source-file="Footer.tsx">
                  Get to Know Us
                </Heading>
                <Stack gap={6} data-sentry-element="Stack" data-sentry-source-file="Footer.tsx">
                  <VStack gap={2} display="flex" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="Footer.tsx">
                    <SafeLink href="/about-us/" data-sentry-element="SafeLink" data-sentry-source-file="Footer.tsx">
                      <ArrowNavItem data-sentry-element="ArrowNavItem" data-sentry-source-file="Footer.tsx">About Us</ArrowNavItem>
                    </SafeLink>
                    <SafeLink href="https://skin-seoul.gorgias.help" data-sentry-element="SafeLink" data-sentry-source-file="Footer.tsx">
                      <ArrowNavItem data-sentry-element="ArrowNavItem" data-sentry-source-file="Footer.tsx">FAQ</ArrowNavItem>
                    </SafeLink>
                  </VStack>
                  <VStack gap={2.5} display="flex" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="Footer.tsx">
                    <Tooltip contentProps={{
                    backgroundColor: 'neutral.90',
                    textStyle: 'paragraph.small'
                  }} positioning={{
                    placement: 'top'
                  }} content="Please be advised that our premises are not open to the public. We operate exclusively as an online retailer. All orders are processed and dispatched for delivery." data-sentry-element="Tooltip" data-sentry-source-file="Footer.tsx">
                      <HStack display="flex" alignItems="flex-start" justify="center" gap="10px" data-sentry-element="HStack" data-sentry-source-file="Footer.tsx">
                        <LucideIcon name="MapPin" aria-hidden="true" boxSize={5} data-sentry-element="LucideIcon" data-sentry-source-file="Footer.tsx" />
                        <Text textStyle="paragraph.small" data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                          23 KEPPEL BAY VIEW, REFLECTIONS AT KEPPEL BAY, Singapore (038987)
                        </Text>
                      </HStack>
                    </Tooltip>

                    <HStack asChild display="flex" alignItems="flex-start" justify="center" gap="10px" data-sentry-element="HStack" data-sentry-source-file="Footer.tsx">
                      <Link href="mailto:support@skin-seoul.com" unstyled textDecoration="underline" data-sentry-element="Link" data-sentry-source-file="Footer.tsx">
                        <LucideIcon name="MailOpen" aria-hidden="true" boxSize={5} data-sentry-element="LucideIcon" data-sentry-source-file="Footer.tsx" />
                        <Text textStyle="paragraph.small" data-sentry-element="Text" data-sentry-source-file="Footer.tsx">support@skin-seoul.com</Text>
                      </Link>
                    </HStack>
                  </VStack>
                </Stack>
              </Flex>
            </Flex>
          </Container>
        </Flex>
      </Box>
      {/* privacy policy  */}
      <Box backgroundColor="generic.100" width="100%" data-sentry-element="Box" data-sentry-source-file="Footer.tsx">
        <Box as="div" color="white" height={{
        base: '113px',
        tablet: '88px',
        desktop: '88px'
      }} maxWidth="1550px" mx="auto" paddingX={4} data-sentry-element="Box" data-sentry-source-file="Footer.tsx">
          <Flex alignItems="center" direction={{
          base: 'column',
          tablet: 'row'
        }} height="100%" justifyContent={{
          base: 'center',
          tablet: 'space-between'
        }} gap={4} data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
            <Text textStyle="paragraph.small" data-sentry-element="Text" data-sentry-source-file="Footer.tsx">©Copyright {new Date().getFullYear()} SkinSeoul</Text>
            <Flex alignItems="center" gap={2} textStyle="paragraph.small" transition="color 0.2s" data-sentry-element="Flex" data-sentry-source-file="Footer.tsx">
              <SafeLink href="/terms-of-service/" aria-label="Terms and conditions" data-sentry-element="SafeLink" data-sentry-source-file="Footer.tsx">
                <Text _hover={{
                color: 'neutral.300'
              }} color="neutral.10" textStyle="paragraph.small" data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                  Terms and conditions
                </Text>
              </SafeLink>
              /
              <SafeLink href="/privacy-policy/" aria-label="Privacy Policy" data-sentry-element="SafeLink" data-sentry-source-file="Footer.tsx">
                <Text _hover={{
                color: 'neutral.300'
              }} color="neutral.10" textStyle="paragraph.small" transition="color 0.2s" data-sentry-element="Text" data-sentry-source-file="Footer.tsx">
                  Privacy Policy
                </Text>
              </SafeLink>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Flex>;
};
export default Footer;